import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { twc } from 'react-twc';

import { ArrowDownImg, BtcImg, EthImg, NearImg, SolanaImg } from '@/assets';
import config from '@/config';
import SelectedNetworkContext from '@/context/SelectedNetwork';
import SymbolInputContext from '@/context/SymbolInputContext';
import { Chain, ChainId, getChainImage } from '@/hooks/useChain';
import { cn } from '@/utils/styles';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import ImageWithLoader from '../image-with-loader';
import { MobileSheet, MobileSheetClose } from '../mobile-sheet';

const { useSymbolInputTracked } = SymbolInputContext;
const { useSelectedNetworkTracked } = SelectedNetworkContext;

function NetworkSwitcher({ chain }: { chain: Exclude<Chain, null> }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [, setSymbolInput] = useSymbolInputTracked();
  const navigate = useNavigate();
  const location = useLocation();

  const [, setSelectedNetwork] = useSelectedNetworkTracked();

  const onClickDropdownItem = (chainId: ChainId) => {
    setIsOpen(false);
    if (!chainId || !config.supportNetworks.includes(chainId)) {
      return;
    }

    // Don't need to switch network when it's already on the current network
    if (chainId === chain?.chainId) {
      return;
    }

    const token =
      config.restakingTokens.find((token) => token.chain === chainId) ?? config.restakingTokens[0];

    setSymbolInput(token);
    setSelectedNetwork([token.chain]);
    navigate(`${location.pathname}?chain=${token.chain}`);
  };

  const networkItemClassName = (chainId: string) =>
    cn(
      chain?.chainId === chainId && 'bg-[#1F242F]',
      !config.supportNetworks.includes(chainId) && 'opacity-50 !cursor-not-allowed',
    );

  const menus = [
    {
      key: 'eth',
      icon: EthImg,
    },
    {
      key: 'near',
      icon: NearImg,
    },
    {
      key: 'solana',
      icon: SolanaImg,
    },
    {
      key: 'btc',
      icon: BtcImg,
      disabled: true,
    },
  ].sort((a, b) => {
    const networks = config.supportNetworks;
    if (
      (networks.includes(a.key) && networks.includes(b.key)) ||
      (!networks.includes(a.key) && !networks.includes(b.key))
    ) {
      return 0;
    } else if (networks.includes(a.key)) {
      return -1;
    } else {
      return 1;
    }
  });

  return (
    <>
      {/* mobile */}
      <div className="flex md:hidden">
        <MobileSheet
          trigger={
            <SelectedNetwork className={cn('!w-[70px] !gap-0 !px-2')}>
              <div className="flex items-center">
                <ImageWithLoader
                  className="w-7 h-7"
                  src={getChainImage(chain.chainId)}
                  width={28}
                  height={28}
                />
              </div>
              <img src={ArrowDownImg} width={18} height={18} />
            </SelectedNetwork>
          }
          content={
            <div className="flex flex-col items-center text-base">
              <div className="mb-5 text-lg font-bold">{t('header.selectNetwork')}</div>
              <div className="flex flex-col w-full">
                {menus.map((item) => {
                  const isActive = chain?.chainId === item.key;
                  return (
                    <MobileSheetClose
                      key={item.key}
                      className={cn(
                        'flex items-center justify-between w-full mb-4 p-3 rounded-md cursor-pointer border border-[rgba(255,255,255,0.14)]',
                        {
                          'bg-[rgba(255,255,255,0.14)]': isActive,
                          'cursor-not-allowed opacity-75': item.disabled,
                        },
                      )}
                      onClick={(ev) => {
                        if (item.disabled) {
                          ev.preventDefault();
                          return;
                        }
                        onClickDropdownItem(item.key as ChainId);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <img src={item.icon} width={22} height={22} alt="" />
                        <p>{item.key.toUpperCase()}</p>
                      </div>
                      {isActive ? <OpenCircle /> : <CloseCircle />}
                    </MobileSheetClose>
                  );
                })}
              </div>
            </div>
          }
        />
      </div>

      {/* pc */}
      <div className="relative hidden md:block">
        <DropdownMenu.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
          <DropdownMenu.Trigger className="outline-none">
            <SelectedNetwork
              className={cn({
                'token-input-shadow': isOpen,
              })}
            >
              <div className="flex items-center gap-3">
                <ImageWithLoader
                  className="w-7 h-7"
                  src={getChainImage(chain.chainId)}
                  width={28}
                  height={28}
                />
                <p>{(chain.chainId ?? '').toUpperCase()}</p>
              </div>
              <img src={ArrowDownImg} width={20} height={20} />
            </SelectedNetwork>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="w-full" sideOffset={20}>
              <DropdownContainer>
                {menus.map((item) => (
                  <DropdownItem
                    key={item.key}
                    className={networkItemClassName(item.key)}
                    onClick={() => {
                      if (item.disabled) return;
                      onClickDropdownItem(item.key as ChainId);
                    }}
                  >
                    <div className="flex gap-2">
                      <img className="w-[22px] h-[22px]" src={item.icon} width={20} height={20} />
                      <p>{item.key.toUpperCase()}</p>
                    </div>
                    {chain?.chainId === item.key ? <OpenCircle /> : <CloseCircle />}
                  </DropdownItem>
                ))}
              </DropdownContainer>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </>
  );
}

const OpenCircle = () => (
  <div className="rounded-full flex w-[10px] h-[10px] bg-[#22FF53] border-2 bg-clip-padding border-white border-opacity-20" />
);
const CloseCircle = () => (
  <div className="rounded-full flex w-[10px] h-[10px] bg-white bg-opacity-20 border-2 border-[#0b0e16]" />
);

const DropdownItem = twc.div`
  flex justify-between items-center gap-2 py-3 px-2
  hover:bg-[#1F242F]
  rounded-md cursor-pointer
`;

const DropdownContainer = twc.div`
  w-full min-w-[160px] outline-none
  rounded-md
  bg-[#0C111D]
  border border-[#1F242F]
  p-2
  flex flex-col gap-2
`;

const SelectedNetwork = twc.div`
  py-2 px-3 h-[44px] w-[160px]
  rounded-full
  bg-white bg-opacity-5
  cursor-pointer select-none
  flex justify-between items-center gap-3
  font-semibold
`;

export default NetworkSwitcher;
